import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { getIDFromUrl } from '@rossum/api-client';
import { useCopyToClipboard } from '@rossum/components/copy-to-clipboard/useCopyToClipboard';
import {
  IconCopy,
  IconDotsVertical,
  IconLayoutGrid,
  IconLayoutList,
  IconLogs,
  IconMailCog,
  IconSTurnRight,
  IconUserSquareRounded,
} from '@rossum/ui/icons/tabler';
import {
  IconButton,
  IconButtonProps,
  Menu,
  SvgIcon,
} from '@rossum/ui/material';
import { isEmbedded } from '../../../constants/config';
import { activitiesExtensionsPath } from '../../../containers/Activities/routes';
import {
  canManageQueueSelector,
  isUserAdmin,
} from '../../../redux/modules/user/selectors';
import { Annotation } from '../../../types/annotation';
import { Document } from '../../../types/document';
import { State } from '../../../types/state';
import { useBackToDocument } from '../document-sidebar/back-to-document/useBackToDocument';
import { ContextActionItem } from '../document-sidebar/sidebar-items/shared/ContextActionItem';

// TODO: These could be useful globally + unify with `getFieldsSettingsPath`
const fieldSettingsUrl = (queueId: number) =>
  `/queues/${queueId}/settings/fields`;

const ruleSettingsUrl = (queueId: number) =>
  `/queues/${queueId}/settings/rules`;

const emailSettingsUrl = (queueId: number) =>
  `/queues/${queueId}/settings/emails`;

const accessSettingsUrl = (queueId: number) =>
  `/queues/${queueId}/settings/access`;

const extensionSettingsUrl = (queueId: number) =>
  `/extensions/my-extensions?events=all&page=1&pageSize=15&view=list&queues=${queueId}`;

const extensionActivitiesUrl = (annotationId: number | undefined) =>
  typeof annotationId === 'number'
    ? `${activitiesExtensionsPath()}?annotation=${annotationId}`
    : null;

type DocumentContextActionsProps = IconButtonProps & {
  document: Document | undefined;
  annotation: Annotation | undefined;
};

export const DocumentContextActions = ({
  document,
  annotation,
  ...iconButtonProps
}: DocumentContextActionsProps) => {
  const intl = useIntl();

  const { triggerBackToDocumentToast } = useBackToDocument();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleOpenMenu: React.MouseEventHandler<HTMLButtonElement> =
    useCallback(e => {
      setAnchorEl(e.currentTarget);
    }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const { copyToClipboard: copyDocumentName } = useCopyToClipboard(
    document?.originalFileName ?? ''
  );

  const documentUrl = window.location.href.split('?')[0] ?? '';

  const { copyToClipboard: copyDocumentUrl } = useCopyToClipboard(documentUrl);

  const handleCopyDocumentName = useCallback(() => {
    copyDocumentName();
    handleCloseMenu();
  }, [copyDocumentName, handleCloseMenu]);

  const handleCopyDocumentUrl = useCallback(() => {
    copyDocumentUrl();
    handleCloseMenu();
  }, [copyDocumentUrl, handleCloseMenu]);

  const queueId = annotation ? getIDFromUrl(annotation.queue) : undefined;
  const annotationId = annotation ? annotation.id : undefined;

  const isAdminLike = useSelector(isUserAdmin);
  const canManageQueue = useSelector((state: State) =>
    canManageQueueSelector(state, annotation?.queue)
  );

  return (
    <>
      <IconButton
        {...iconButtonProps}
        size="medium"
        color="secondary"
        onClick={handleOpenMenu}
        data-testid="more-actions-button"
      >
        <SvgIcon fontSize="medium">
          <IconDotsVertical />
        </SvgIcon>
      </IconButton>
      <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={handleCloseMenu}>
        <ContextActionItem
          icon={
            <SvgIcon fontSize="medium">
              <IconCopy />
            </SvgIcon>
          }
          onClick={handleCopyDocumentName}
        >
          {intl.formatMessage({
            id: 'components.documentContextActions.copyName',
          })}
        </ContextActionItem>
        {!isEmbedded() ? (
          <ContextActionItem
            icon={
              <SvgIcon fontSize="medium">
                <IconCopy />
              </SvgIcon>
            }
            onClick={handleCopyDocumentUrl}
          >
            {intl.formatMessage({
              id: 'components.documentContextActions.copyUrl',
            })}
          </ContextActionItem>
        ) : null}
        {!isEmbedded() && queueId ? (
          <>
            {isAdminLike ? (
              <>
                <ContextActionItem
                  icon={
                    <SvgIcon fontSize="medium">
                      <IconLayoutList />
                    </SvgIcon>
                  }
                  component={RouterLink}
                  onClick={triggerBackToDocumentToast}
                  to={fieldSettingsUrl(queueId)}
                >
                  {intl.formatMessage({
                    id: 'components.documentContextActions.fieldsSettings',
                  })}
                </ContextActionItem>
                <ContextActionItem
                  icon={
                    <SvgIcon fontSize="medium">
                      <IconMailCog />
                    </SvgIcon>
                  }
                  component={RouterLink}
                  onClick={triggerBackToDocumentToast}
                  to={emailSettingsUrl(queueId)}
                >
                  {intl.formatMessage({
                    id: 'components.documentContextActions.emailSettings',
                  })}
                </ContextActionItem>
              </>
            ) : null}
            {canManageQueue ? (
              <>
                <ContextActionItem
                  icon={
                    <SvgIcon fontSize="medium">
                      <IconSTurnRight />
                    </SvgIcon>
                  }
                  component={RouterLink}
                  onClick={triggerBackToDocumentToast}
                  to={ruleSettingsUrl(queueId)}
                >
                  {intl.formatMessage({
                    id: 'components.documentContextActions.rulesSettings',
                  })}
                </ContextActionItem>
                <ContextActionItem
                  icon={
                    <SvgIcon fontSize="medium">
                      <IconUserSquareRounded />
                    </SvgIcon>
                  }
                  component={RouterLink}
                  onClick={triggerBackToDocumentToast}
                  to={accessSettingsUrl(queueId)}
                >
                  {intl.formatMessage({
                    id: 'components.documentContextActions.accessSettings',
                  })}
                </ContextActionItem>
              </>
            ) : null}
            {isAdminLike ? (
              <>
                <ContextActionItem
                  icon={
                    <SvgIcon fontSize="medium">
                      <IconLayoutGrid />
                    </SvgIcon>
                  }
                  component={RouterLink}
                  onClick={triggerBackToDocumentToast}
                  to={extensionSettingsUrl(queueId)}
                >
                  {intl.formatMessage({
                    id: 'components.documentContextActions.extensionSettings',
                  })}
                </ContextActionItem>
                <ContextActionItem
                  icon={
                    <SvgIcon fontSize="medium">
                      <IconLogs />
                    </SvgIcon>
                  }
                  component={RouterLink}
                  onClick={triggerBackToDocumentToast}
                  to={() => extensionActivitiesUrl(annotationId)}
                >
                  {intl.formatMessage({
                    id: 'components.documentContextActions.extensionActivities',
                  })}
                </ContextActionItem>
              </>
            ) : null}
          </>
        ) : null}
      </Menu>
    </>
  );
};
